import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import { App } from "./App";
import { AuthProvider } from "./auth/AuthContext";
import { ToastProvider } from "./components/Toast";
import { ApiError } from "./api/client";
import { applyTheme, readStoredTheme } from "./lib/theme";
import "./index.css";

// Before first paint, so there's no flash of the wrong theme.
applyTheme(readStoredTheme());

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Every screen reads shared, multi-user data — a colleague may have
      // changed a record a second ago — so nothing is treated as fresh and
      // refocusing a tab refetches.
      staleTime: 0,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // A rejected token or an invalid request will fail identically however
        // many times it's retried; only transport failures are worth another go.
        if (error instanceof ApiError && error.status !== 0) return false;
        return failureCount < 2;
      },
    },
  },
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ToastProvider>
            <App />
          </ToastProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
